/**
 * MBA Lab — the Stakeholder Interview Builder's goal-anchored question bank.
 * Pure data, no functions, matching readiness-dimensions.js's precedent.
 * Every question is original - not adapted from any third-party interview
 * guide or consulting-assignment material.
 */

export const GOALS = Object.freeze([
  {
    key: 'impact',
    label: 'Understand impact',
    helpText: 'How this actually changes their day-to-day work.',
  },
  {
    key: 'concerns',
    label: 'Surface concerns',
    helpText: "What worries them that they might not volunteer unprompted.",
  },
  {
    key: 'requirements',
    label: 'Gather requirements',
    helpText: 'What they actually need for this to work for them.',
  },
  {
    key: 'buyIn',
    label: 'Build buy-in',
    helpText: 'Give them a real, specific way to shape the outcome.',
  },
  {
    key: 'diagnose',
    label: 'Diagnose a problem',
    helpText: 'Understand a specific issue from their vantage point.',
  },
]);

export const QUESTIONS = Object.freeze([
  // Understand impact
  { id: 'imp-1', goal: 'impact', text: 'Walk me through how this actually touches your work today, step by step.' },
  { id: 'imp-2', goal: 'impact', text: 'What would change first for you, and what would change last?' },
  { id: 'imp-3', goal: 'impact', text: 'Is there a part of your job this makes harder, not just different?' },
  { id: 'imp-4', goal: 'impact', text: 'Who else on your team would feel this before you would?' },
  { id: 'imp-5', goal: 'impact', text: "What would \"this went well\" look like from where you sit?" },
  // Surface concerns
  { id: 'con-1', goal: 'concerns', text: "What's the version of this that would actually worry you?" },
  { id: 'con-2', goal: 'concerns', text: "What haven't you been asked that you wish someone would ask?" },
  { id: 'con-3', goal: 'concerns', text: "Is there a past version of something like this that didn't go well? What happened?" },
  { id: 'con-4', goal: 'concerns', text: 'What would make you quietly stop trusting this process?' },
  { id: 'con-5', goal: 'concerns', text: "If this fails, what's the most likely reason, in your honest opinion?" },
  // Gather requirements
  { id: 'req-1', goal: 'requirements', text: 'What would you need to be true for this to actually work for you?' },
  { id: 'req-2', goal: 'requirements', text: "What's non-negotiable from your side, versus just a preference?" },
  { id: 'req-3', goal: 'requirements', text: "What's missing that you'd need before you could commit to this?" },
  { id: 'req-4', goal: 'requirements', text: "Who else needs to sign off, that we might not have on our list?" },
  { id: 'req-5', goal: 'requirements', text: "What does \"ready\" look like from your seat, specifically?" },
  // Build buy-in
  { id: 'buy-1', goal: 'buyIn', text: 'What part of this would you want real influence over, not just a heads-up about?' },
  { id: 'buy-2', goal: 'buyIn', text: 'What would make this feel like something that happened with you, not to you?' },
  { id: 'buy-3', goal: 'buyIn', text: 'If you were leading this, what would you do differently?' },
  { id: 'buy-4', goal: 'buyIn', text: "What's one thing we could change right now that would make this easier to support?" },
  { id: 'buy-5', goal: 'buyIn', text: 'What would you need to be able to say to your own team about this?' },
  // Diagnose a problem
  { id: 'dia-1', goal: 'diagnose', text: 'When did you first notice this was a problem?' },
  { id: 'dia-2', goal: 'diagnose', text: 'What have you already tried, and what happened?' },
  { id: 'dia-3', goal: 'diagnose', text: "Who else is affected by this the way you are?" },
  { id: 'dia-4', goal: 'diagnose', text: "What's the story people tell each other about why this keeps happening?" },
  { id: 'dia-5', goal: 'diagnose', text: 'If you could fix exactly one part of this, what would it be?' },
]);
